import { ShoppingBag, Loader2, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard, Product } from './ProductCard';

interface ProductGridProps {
  products: Product[];
  loading: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: string, source: 'products' | 'recommendations') => void;
  onDuplicate?: (product: Product) => void;
  onAddToProducts?: (product: Product) => void;
  onAddNew?: () => void;
  isDuplicating?: boolean;
  selectedProductId?: string;
  searchQuery?: string;
  filterSource?: 'products' | 'recommendations';
  isSelectionMode?: boolean;
  selectedProducts?: Product[];
  onSelectProduct?: (product: Product, isSelected: boolean) => void;
}

export function ProductGrid({ 
  products, 
  loading, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onAddToProducts,
  onAddNew,
  isDuplicating,
  selectedProductId,
  searchQuery,
  filterSource,
  isSelectionMode = false,
  selectedProducts = [],
  onSelectProduct
}: ProductGridProps) {
  // Filter products based on source if specified
  const filteredProducts = filterSource 
    ? products.filter(p => p.source === filterSource)
    : products;

  // Deduplicate products that appear in both products and recommendations tables
  const deduplicatedProducts = removeDuplicates(filteredProducts);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2 text-muted-foreground">Loading products...</p>
      </div>
    );
  }

  if (deduplicatedProducts.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        {searchQuery ? (
          <p>No products match your search</p>
        ) : (
          <div className="space-y-3">
            <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p>No {filterSource || ''} products have been added yet</p>
            {onAddNew && (
              <Button variant="outline" size="sm" onClick={onAddNew}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add your first product
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {deduplicatedProducts.map(product => {
        const isSelected = selectedProducts.some(
          p => p.id === product.id && p.source === product.source
        );
        
        return (
          <ProductCard
            key={`${product.source}-${product.id}`}
            product={product}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onAddToProducts={onAddToProducts}
            isDuplicating={isDuplicating}
            selectedProductId={selectedProductId}
            isSelectable={isSelectionMode}
            isSelected={isSelected}
            onSelectChange={onSelectProduct}
          />
        );
      })}
    </div>
  );
}

// Helper function to remove duplicate products
// Priority: Keep products from 'products' table when duplicates are found
function removeDuplicates(products: Product[]): Product[] {
  // Create a map to track products by a unique combination of name and affiliate link
  const productMap = new Map<string, Product>();
  
  // Process products in reverse order to prioritize 'products' over 'recommendations'
  // when we have both versions of the same product
  [...products].reverse().forEach(product => {
    // Create a unique key from name + affiliate link (normalized)
    const key = `${product.name.toLowerCase()}-${product.affiliate_link.toLowerCase()}`;
    
    // When adding to the map, products from 'products' table will overwrite
    // duplicates from 'recommendations' table due to the reverse order
    productMap.set(key, product);
  });
  
  // Convert the map values back to an array and sort to maintain the original order
  const uniqueProducts = Array.from(productMap.values());
  
  // Sort the products to match original order
  return uniqueProducts.sort((a, b) => {
    // First by source ('products' first, then 'recommendations')
    if (a.source !== b.source) {
      return a.source === 'products' ? -1 : 1;
    }
    
    // Then by creation date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}
